import React from 'react';

import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useExtensionContext } from '~/components/extension.context';
import type { OnboardingTourName, OnboardingTourStatusMap } from '~/components/Onboarding/onboarding.model';
import { onboardingTourNameEnum, onboardingTourNames } from '~/components/Onboarding/onboarding.model';

type ContextProps = [
  {
    currentTourName?: OnboardingTourName;
    nextTourName?: OnboardingTourName;
    statuses: OnboardingTourStatusMap;
    fakeTakeAction?: boolean;
    shouldShowChecklist?: boolean;
    isTourActive?: boolean;
  },
  {
    isTourCompleted: (tourName: OnboardingTourName) => boolean;
    resetOnboarding: () => void;
    checkIfExtensionExists: () => void;
    setShouldShowChecklist: (visible: boolean) => void;
  },
];

const defaultValue: ContextProps = [
  {
    currentTourName: undefined,
    statuses: {},
    shouldShowChecklist: true,
  },
  {
    isTourCompleted: () => false,
    resetOnboarding: () => console.error('OnboardingProvider not initialized'),
    checkIfExtensionExists: () => console.error('OnboardingProvider not initialized'),
    setShouldShowChecklist: () => console.error('OnboardingProvider not initialized'),
  },
];

const OnboardingContext = React.createContext<ContextProps>(defaultValue);

type OnboardingProviderProps = {
  children: React.ReactNode;
};

const defaultStatuses: OnboardingTourStatusMap = {
  InstallExtension: false,
  SetupProfile: false,
  CreateYourFirstCampaign: false,
  TakeAction: false,
  InviteTeamMembers: false,
  SetUpIntegrations: false,
};

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const [{ extensionConnectedStatus }] = useExtensionContext();
  const [isStepVisible] = React.useState<boolean>(false);
  const [statuses, setStatuses] = useLocalStorage<OnboardingTourStatusMap>('onboardingStatuses', defaultStatuses);
  const [shouldShowChecklist, setShouldShowChecklist] = useLocalStorage<boolean>('onboardingShouldShowChecklist', true);

  const isTourCompleted = React.useCallback(
    (tourName: OnboardingTourName) => {
      return statuses?.[tourName] === true;
    },
    [statuses],
  );

  const storeTourStatus = React.useCallback(
    (tourName: OnboardingTourName, isComplete: boolean) => {
      setStatuses((statuses: OnboardingTourStatusMap) => ({
        ...statuses,
        [tourName]: isComplete,
      }));
    },
    [setStatuses],
  );

  const checkIfExtensionExists = React.useCallback(() => {
    const exists = extensionConnectedStatus === 'connected';
    storeTourStatus(onboardingTourNameEnum.InstallExtension, exists);
  }, [storeTourStatus, extensionConnectedStatus]);

  const [currentTourName, nextTourName] = React.useMemo(() => {
    let currentTourName;
    let nextTourName;

    for (const [index, name] of onboardingTourNames.entries()) {
      if (!statuses?.[name]) {
        currentTourName = name;
        nextTourName = onboardingTourNames[index + 1];
        break;
      }
    }

    return [currentTourName, nextTourName];
  }, [statuses]);

  const resetOnboarding = React.useCallback(() => {
    setStatuses(defaultStatuses);
    setShouldShowChecklist(true);
  }, [setShouldShowChecklist, setStatuses]);

  const fakeTakeAction = currentTourName === onboardingTourNameEnum.TakeAction && isStepVisible;

  React.useEffect(() => {
    checkIfExtensionExists();
  }, [checkIfExtensionExists]);

  const value: ContextProps = [
    {
      currentTourName, // currently tour to start/end
      nextTourName, // next tour to be started
      statuses,
      fakeTakeAction,
      shouldShowChecklist,
      isTourActive: isStepVisible,
    },
    {
      isTourCompleted,
      resetOnboarding,
      checkIfExtensionExists,
      setShouldShowChecklist,
    },
  ];
  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboardingContext = () => React.useContext(OnboardingContext);
